import AppConfig from 'app-config';
import { AuthError, authError } from './errors';
import { authRequest } from './transport';
import {
  BrowserContext,
  Challenge,
  LoginResponse,
  RefreshResponse,
  SessionMetadata
} from './types';

type SessionEvent = 'token' | 'cleared' | 'replaced' | 'challenge' | 'error';
type TokenState = { token: string; accessExpiresAt: number; revision: string };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class SessionCoordinator {
  readonly key = `auth:v2:pbb:${AppConfig.environment}:${AppConfig.AuthMSBaseUrl}`;

  private channel?: BroadcastChannel;

  private started = false;

  private pending?: Promise<string>;

  private tokenState?: TokenState;

  private listeners = new Set<(event: SessionEvent, error?: AuthError) => void>();

  private timer?: ReturnType<typeof setTimeout>;

  private retryAt = 0;

  private knownSessionId?: string;

  subscribe(listener: (event: SessionEvent, error?: AuthError) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: SessionEvent, error?: AuthError) {
    this.listeners.forEach((listener) => listener(event, error));
  }

  start() {
    if (this.started) return;
    if (
      typeof window === 'undefined' ||
      !navigator.locks ||
      !window.BroadcastChannel ||
      !window.crypto?.randomUUID
    ) {
      throw new AuthError(
        'Use a current browser with secure storage and cookies enabled to sign in.'
      );
    }
    try {
      [localStorage, sessionStorage].forEach((storage) => {
        storage.setItem(`${this.key}:probe`, '1');
        storage.removeItem(`${this.key}:probe`);
      });
      // Only remove this application's obsolete state, never sessionStorage.clear().
      ['auth.admin_token', 'auth.alem'].forEach((key) => sessionStorage.removeItem(key));
      localStorage.removeItem('pbb-tm');
      this.channel = new BroadcastChannel(this.key);
    } catch {
      throw new AuthError('Enable browser storage to sign in.');
    }
    this.started = true;
    this.knownSessionId = this.metadata?.id;
    this.channel.onmessage = ({ data }) => {
      if (data.type === 'request-token') {
        if (this.validToken(0)) this.broadcast('token', this.tokenState);
      } else if (data.type === 'token') {
        const meta = this.metadata;
        if (
          meta &&
          data.value?.revision === meta.revision &&
          typeof data.value.token === 'string'
        ) {
          const replaced = this.knownSessionId !== meta.id;
          this.knownSessionId = meta.id;
          this.tokenState = data.value;
          this.schedule();
          this.emit(replaced ? 'replaced' : 'token');
        }
      } else if (data.type === 'challenge') {
        sessionStorage.removeItem(`${this.key}:challenge`);
        this.emit('challenge');
      } else if (data.type === 'cleared' && !this.metadata) {
        this.clearMemory();
        sessionStorage.removeItem(`${this.key}:challenge`);
        this.emit('cleared');
      }
    };
    window.addEventListener('storage', this.storageChanged);
    document.addEventListener('visibilitychange', this.foreground);
    window.addEventListener('focus', this.foreground);
  }

  // Useful for tests and hot reload; production initializes one coordinator.
  dispose() {
    this.channel?.close();
    clearTimeout(this.timer);
    window.removeEventListener('storage', this.storageChanged);
    document.removeEventListener('visibilitychange', this.foreground);
    window.removeEventListener('focus', this.foreground);
    this.started = false;
    this.clearMemory();
    this.retryAt = 0;
  }

  private storageChanged = (event: StorageEvent) => {
    if (event.key !== `${this.key}:session` && event.key !== null) return;
    const meta = this.metadata;
    if (!meta) {
      this.clearMemory();
      this.emit('cleared');
    } else if (this.knownSessionId && meta.id !== this.knownSessionId) {
      this.clearMemory();
      this.knownSessionId = meta.id;
      this.emit('replaced');
    }
  };

  private foreground = () => {
    if (document.visibilityState === 'visible' && this.metadata) {
      this.getAccessToken().catch((error) => this.emit('error', authError(error)));
    }
  };

  private broadcast(type: string, value?: unknown) {
    this.channel?.postMessage({ type, value });
  }

  get metadata(): SessionMetadata | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
      const value = JSON.parse(localStorage.getItem(`${this.key}:session`) || 'null');
      return value &&
        typeof value.id === 'string' &&
        typeof value.csrf === 'string' &&
        value.csrf &&
        typeof value.revision === 'string' &&
        Number.isFinite(value.expiresAt)
        ? value
        : undefined;
    } catch {
      return undefined;
    }
  }

  get accessToken() {
    return this.validToken(0) ? this.tokenState.token : '';
  }

  get accessExpiresAt() {
    return this.tokenState?.accessExpiresAt || 0;
  }

  browserId(create = false): string {
    this.start();
    const key = 'auth:browser-id';
    let value = localStorage.getItem(key);
    if (!value || !UUID.test(value)) {
      if (!create || this.metadata) {
        this.clear();
        throw new AuthError('Browser identity was lost. Please sign in again.', 401);
      }
      value = window.crypto.randomUUID();
      localStorage.setItem(key, value);
    }
    return value;
  }

  browserContext(): BrowserContext {
    const dimension = (n: number) => Math.max(1, Math.min(32768, Math.floor(n) || 1));
    return {
      browserId: this.browserId(true),
      language: (navigator.language || 'en').slice(0, 64),
      timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC').slice(0, 128),
      screen: { width: dimension(window.screen.width), height: dimension(window.screen.height) }
    };
  }

  get challenge(): Challenge | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
      const value: Challenge = JSON.parse(
        sessionStorage.getItem(`${this.key}:challenge`) || 'null'
      );
      return value &&
        value.id === localStorage.getItem(`${this.key}:flow`) &&
        value.expiresAt > Date.now() &&
        value.csrf &&
        value.attempts < 5
        ? value
        : undefined;
    } catch {
      return undefined;
    }
  }

  saveChallenge(challenge: Challenge) {
    sessionStorage.setItem(`${this.key}:challenge`, JSON.stringify(challenge));
  }

  beginChallenge() {
    this.start();
    const id = window.crypto.randomUUID();
    localStorage.setItem(`${this.key}:flow`, id);
    sessionStorage.removeItem(`${this.key}:challenge`);
    this.broadcast('challenge');
    return id;
  }

  abandonChallenge() {
    sessionStorage.removeItem(`${this.key}:challenge`);
  }

  async exclusive<T>(operation: () => Promise<T>): Promise<T> {
    this.start();
    return navigator.locks.request(this.key, operation);
  }

  private validToken(margin = 30000) {
    const meta = this.metadata;
    return !!(
      meta &&
      meta.expiresAt > Date.now() &&
      this.tokenState &&
      this.tokenState.revision === meta.revision &&
      this.tokenState.accessExpiresAt > Date.now() + margin
    );
  }

  private saveToken(data: RefreshResponse, csrf: string) {
    if (
      !data?.access_token ||
      !data.session_id ||
      !Number.isFinite(data.expires_in) ||
      data.expires_in <= 0 ||
      !Number.isFinite(Date.parse(data.expires_at)) ||
      !csrf
    ) {
      throw new AuthError('Invalid session response. Please sign in again.', 401);
    }
    const meta: SessionMetadata = {
      id: data.session_id,
      csrf,
      expiresAt: Date.parse(data.expires_at),
      revision: window.crypto.randomUUID()
    };
    this.tokenState = {
      token: data.access_token,
      accessExpiresAt: Date.now() + data.expires_in * 1000,
      revision: meta.revision
    };
    localStorage.setItem(`${this.key}:session`, JSON.stringify(meta));
    this.knownSessionId = meta.id;
    this.retryAt = 0;
    localStorage.removeItem(`${this.key}:retry-after`);
    this.broadcast('token', this.tokenState);
    this.schedule();
    this.emit('token');
  }

  acceptLogin(data: LoginResponse) {
    this.saveToken(data, data.csrf_token);
    localStorage.removeItem(`${this.key}:rotation`);
    localStorage.removeItem(`${this.key}:flow`);
    this.abandonChallenge();
  }

  private schedule() {
    clearTimeout(this.timer);
    if (!this.tokenState) return;
    this.timer = setTimeout(() => {
      if (document.visibilityState === 'visible') this.foreground();
    }, Math.max(1000, this.tokenState.accessExpiresAt - Date.now() - 30000));
  }

  async getAccessToken(rejectedToken?: string): Promise<string> {
    this.start();
    if (this.metadata) this.browserId();
    if (this.validToken() && (!rejectedToken || this.tokenState.token !== rejectedToken))
      return this.tokenState.token;
    if (this.pending) return this.pending;
    this.pending = this.exclusive(async () => {
      let meta = this.metadata;
      if (!meta || meta.expiresAt <= Date.now()) {
        this.clear();
        throw new AuthError('Your session has expired. Please sign in again.', 401);
      }
      const browserId = this.browserId();
      // A marker left after lock acquisition means the previous owner did not finish safely.
      if (localStorage.getItem(`${this.key}:rotation`)) {
        this.clear();
        throw new AuthError('Session refresh was interrupted. Please sign in again.', 401);
      }
      if (this.validToken() && (!rejectedToken || this.tokenState.token !== rejectedToken))
        return this.tokenState.token;
      this.broadcast('request-token');
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
      if (this.validToken() && (!rejectedToken || this.tokenState.token !== rejectedToken))
        return this.tokenState.token;
      if (
        Date.now() <
        Math.max(this.retryAt, Number(localStorage.getItem(`${this.key}:retry-after`)) || 0)
      )
        throw new AuthError('Please wait before retrying session refresh.', 429);
      meta = this.metadata;
      if (!meta) throw new AuthError('Please sign in again.', 401);
      localStorage.setItem(`${this.key}:rotation`, meta.revision);
      try {
        const data = await authRequest<RefreshResponse>(
          '/auth/refresh_token',
          { browser: { browserId } },
          { csrf: meta.csrf }
        );
        if (this.metadata?.revision !== meta.revision || data.session_id !== meta.id) {
          throw new AuthError('Session changed during refresh. Please sign in again.', 401);
        }
        this.saveToken(data, meta.csrf);
        localStorage.removeItem(`${this.key}:rotation`);
        return this.tokenState.token;
      } catch (error) {
        const failure = authError(error);
        if (!failure.status || failure.status === 401) {
          this.clear();
          throw new AuthError('Session refresh could not be confirmed. Please sign in again.', 401);
        }
        // An explicit rejection is not evidence of logout; never automatically retry it.
        localStorage.removeItem(`${this.key}:rotation`);
        this.retryAt = Date.now() + (failure.status === 429 ? 60000 : 30000);
        localStorage.setItem(`${this.key}:retry-after`, String(this.retryAt));
        throw failure;
      }
    }).finally(() => {
      this.pending = undefined;
    });
    return this.pending;
  }

  async authenticated<T>(
    path: string,
    body?: unknown,
    method: 'GET' | 'POST' = 'POST',
    revoke = false
  ): Promise<T> {
    const token = await this.getAccessToken();
    return this.exclusive(async () => {
      if (!this.accessToken || this.accessToken !== token) {
        throw new AuthError('Session changed. Please try again.', 409);
      }
      const result = await authRequest<T>(path, body, {
        method,
        token,
        ...(method === 'POST' ? { csrf: this.metadata.csrf } : {})
      });
      if (revoke) this.clear();
      return result;
    });
  }

  private clearMemory() {
    this.tokenState = undefined;
    this.knownSessionId = undefined;
    clearTimeout(this.timer);
  }

  clear() {
    this.clearMemory();
    ['session', 'rotation', 'flow', 'retry-after'].forEach((suffix) =>
      localStorage.removeItem(`${this.key}:${suffix}`)
    );
    this.abandonChallenge();
    this.broadcast('cleared');
    this.emit('cleared');
  }
}

export const session = new SessionCoordinator();
